import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

import { App, Button, Col, Form, Input, Row } from 'antd';

import PhoneSelect from 'components/PhoneSelect/PhoneSelect';
import ReCaptcha, { ReCaptchaRef } from 'components/ReCaptcha/ReCaptcha';

import useErrorCatch from 'hooks/useErrorCatch';
import { useTrackForm } from 'hooks/useTrackForm';

import Services from 'utils/services';

import { SECTION_ROW_GUTTER } from 'constants/commons';
import { GTMEvents } from 'constants/enums';

import { ReactComponent as ArrowRight } from 'assets/icons/arrowRight.svg';

import styles from './ContactUsForm.module.scss';

type ContactUsFormProps = {
  setIsShowThankYouMessage: Dispatch<SetStateAction<boolean>>;
};

const ContactUsForm: FC<ContactUsFormProps> = ({
  setIsShowThankYouMessage
}) => {
  const catchError = useErrorCatch();
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fieldsValues = Form.useWatch([], form);
  const recaptchaRef = useRef<ReCaptchaRef>(null);

  useTrackForm({
    formId: 'contact-us',
    incompleteEvent: GTMEvents.HomeContactFormAbandonment,
    submittedEvent: GTMEvents.HomeContactFormSubmit
  });

  const handleSubmitForm = async () => {
    try {
      await form.validateFields();

      if (recaptchaRef.current) {
        recaptchaRef.current.execute();
      }
    } catch (e) {
      catchError({
        default: { message: 'Please fill in all required fields correctly' }
      });
      return;
    }
  };

  const handleCaptchaSuccess = async () => {
    setIsLoading(true);
    const data = form.getFieldsValue();
    try {
      await Services.postContactUs(data);
      setIsShowThankYouMessage(true);
      form.resetFields();
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      notification.error({
        message: "Can't submit form, please try again later",
        placement: 'topRight'
      });
    } finally {
      setIsLoading(false);
      resetThankYouMessage();
    }
  };

  const onCaptchaChange = async (value: string | null): Promise<void> => {
    if (value) {
      handleCaptchaSuccess();
    }
  };

  const handleExpired = (): void => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleError = (): void => {
    console.error('reCAPTCHA error occurred');
  };

  const resetThankYouMessage = (): NodeJS.Timeout => {
    return setTimeout(() => {
      setIsShowThankYouMessage(false);
    }, 5000);
  };

  useEffect(() => {
    const timer = resetThankYouMessage();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    form.validateFields({ validateOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsValues]);

  return (
    <div>
      <Form
        id="contact-us"
        form={form}
        scrollToFirstError
        name="contactUs"
        autoComplete="off"
        onFinish={handleSubmitForm}
      >
        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={12} xs={24}>
            <Form.Item
              name="firstName"
              label="First name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Please enter First Name" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              name="lastName"
              label="Last name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Please enter Last Name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={12} xs={24}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[{ type: 'email', required: true }]}
            >
              <Input placeholder="Please enter Email Address" />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              name="phone"
              label="Phone number"
              rules={[{ required: true }]}
            >
              <PhoneSelect name="phone" form={form} className="phone-select" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col span={24}>
            <Form.Item
              name="message"
              label="Message"
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={7} placeholder="Please enter message" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col span={24} className={styles.submitContainer}>
            <Button
              className={styles.submitButton}
              type="primary"
              icon={<ArrowRight />}
              iconPosition="end"
              htmlType="submit"
              loading={isLoading}
            >
              Submit
            </Button>
          </Col>
        </Row>
        <ReCaptcha
          ref={recaptchaRef}
          onChange={onCaptchaChange}
          onError={handleError}
          onExpired={handleExpired}
        />
      </Form>
    </div>
  );
};

export default ContactUsForm;
